import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Container, Row, Col } from "react-bootstrap";
import { useAuth, hasRole } from "../../context/AuthContext";
import {
  listJuniors,
  listPendingJuniors,
  updateJuniorStatus,
  addJunior,
  updateJunior,
  deleteJunior,
  listProjects,
  assignJuniorToProject,
} from "../../lib/firebase";
import { approveJunior, rejectJunior } from "../../lib/functions";
import SEED_JUNIORS from "../../data/staffing/seed-juniors";
import Instructions from "../../components/Instructions/Instructions";

const SectionStyled = styled.section`
  padding: 96px 0;
  background: var(--nd-black);
`;

const Label = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin: 0 0 16px;
`;

const Title = styled.h1`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 48px;
  font-weight: 700;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0 0 12px;
  @media (max-width: 768px) { font-size: 32px; }
`;

const Subtitle = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin: 0 0 32px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  align-items: center;
`;

const Chip = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid ${({ active }) => active ? "var(--nd-text-display)" : "var(--nd-border-visible)"};
  background: ${({ active }) => active ? "var(--nd-text-display)" : "transparent"};
  color: ${({ active }) => active ? "var(--nd-black)" : "var(--nd-text-disabled)"};
  cursor: pointer;
  outline: none;
`;

const SearchInput = styled.input`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--nd-border-visible);
  color: var(--nd-text-primary);
  padding: 8px 4px;
  margin-bottom: 32px;
  outline: none;
  width: 100%;
  max-width: 360px;
  &::placeholder { color: var(--nd-text-disabled); }
  &:focus { border-color: var(--nd-text-display); }
`;

const Card = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  height: 100%;
  transition: border-color 200ms ease;
  &:hover { border-color: var(--nd-border-visible); }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const CardName = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin: 0;
`;

const StatusBadge = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid var(--nd-border-visible);
  color: ${({ status }) =>
    status === "available" ? "var(--nd-success)" :
    status === "assigned" ? "var(--nd-warning)" :
    "var(--nd-text-disabled)"};
`;

const CardMeta = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 0 0 12px;
`;

const Bio = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin: 0 0 16px;
`;

const SkillRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const SkillChip = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  color: var(--nd-text-secondary);
  background: rgba(255,255,255,0.03);
  padding: 2px 8px;
  border-radius: 4px;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Rate = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  color: var(--nd-text-disabled);
`;

const AssignButton = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 8px 16px;
  border-radius: 999px;
  border: none;
  background: var(--nd-text-display);
  color: var(--nd-black);
  cursor: pointer;
  outline: none;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

const Empty = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  padding: 48px 0;
  text-align: center;
`;

const AdminToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

const AddBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 8px 16px;
  border-radius: 999px;
  border: none;
  background: var(--nd-text-display);
  color: var(--nd-black);
  cursor: pointer;
  outline: none;
  &:hover { opacity: 0.85; }
`;

const FormCard = styled.form`
  background: var(--nd-surface-raised);
  border: 1px solid var(--nd-border-visible);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const FormField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const FormInput = styled.input`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  background: var(--nd-surface);
  border: 1px solid var(--nd-border-visible);
  border-radius: 6px;
  color: var(--nd-text-primary);
  padding: 8px 10px;
  outline: none;
  &:focus { border-color: var(--nd-text-display); }
`;

const FormTextArea = styled.textarea`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  background: var(--nd-surface);
  border: 1px solid var(--nd-border-visible);
  border-radius: 6px;
  color: var(--nd-text-primary);
  padding: 8px 10px;
  outline: none;
  min-height: 60px;
  resize: vertical;
  &:focus { border-color: var(--nd-text-display); }
`;

const FormSelect = styled.select`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  background: var(--nd-surface);
  border: 1px solid var(--nd-border-visible);
  border-radius: 6px;
  color: var(--nd-text-primary);
  padding: 8px 10px;
  outline: none;
`;

const FormActions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const FormSaveBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 8px 16px;
  border-radius: 999px;
  border: none;
  background: var(--nd-text-display);
  color: var(--nd-black);
  cursor: pointer;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const FormCancelBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid var(--nd-border-visible);
  background: transparent;
  color: var(--nd-text-secondary);
  cursor: pointer;
  &:hover { color: var(--nd-text-display); border-color: var(--nd-text-display); }
`;

const WantsToLearnLabel = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 0 0 6px;
`;

const CardAdminRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const SmallBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid var(--nd-border);
  background: transparent;
  color: var(--nd-text-disabled);
  cursor: pointer;
  outline: none;
  &:hover { color: var(--nd-text-display); border-color: var(--nd-text-display); }
`;

const DangerBtn = styled(SmallBtn)`
  &:hover { color: var(--nd-accent); border-color: var(--nd-accent); }
`;

const PendingSection = styled.div`
  margin-bottom: 48px;
`;

const PendingKicker = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-warning);
  margin: 0 0 16px;
`;

const PendingCardAdminRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const ApproveBtn = styled(SmallBtn)`
  &:hover { color: var(--nd-success); border-color: var(--nd-success); }
`;

const AssignField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const AssignLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const AssignSelect = styled.select`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  background: var(--nd-surface);
  border: 1px solid var(--nd-border-visible);
  border-radius: 6px;
  color: var(--nd-text-primary);
  padding: 6px 10px;
  outline: none;
  &:focus { border-color: var(--nd-text-display); }
`;

const AssignError = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-accent);
`;

const FILTERS = ["all", "available", "assigned", "off"];
const STATUSES = ["available", "assigned", "off"];

const emptyDraft = () => ({
  name: "",
  role: "DevOps Junior",
  location: "",
  yearsOfExperience: 1,
  skills: "",
  rate: "",
  bio: "",
  status: "available",
});

const toDraft = (j) => ({
  id: j.id,
  name: j.name || "",
  role: j.role || "DevOps Junior",
  location: j.location || "",
  yearsOfExperience: j.yearsOfExperience || 0,
  skills: Array.isArray(j.skills) ? j.skills.join(", ") : "",
  rate: j.rate || "",
  bio: j.bio || "",
  status: j.status || "available",
});

const draftToDoc = (draft) => ({
  name: draft.name.trim(),
  role: draft.role.trim(),
  location: draft.location.trim(),
  yearsOfExperience: Number(draft.yearsOfExperience) || 0,
  skills: draft.skills.split(",").map((s) => s.trim()).filter(Boolean),
  rate: draft.rate.trim(),
  bio: draft.bio.trim(),
  status: draft.status,
  availability: draft.status,
});

const JuniorStaffing = () => {
  const { role } = useAuth();
  const isAdmin = hasRole(role, "admin");
  const [juniors, setJuniors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // "new" | {id, ...draft}
  const [saving, setSaving] = useState(false);
  const [usingSeed, setUsingSeed] = useState(false);
  const [assignErrors, setAssignErrors] = useState({});
  const [pendingJuniors, setPendingJuniors] = useState([]);
  const [pendingActions, setPendingActions] = useState({}); // { [cardId]: "approving"|"rejecting"|"error:<msg>" }

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    listPendingJuniors().then((rows) => {
      if (cancelled) return;
      setPendingJuniors(rows || []);
    }).catch((err) => {
      console.error("[staffing] listPendingJuniors failed:", err);
    });
    return () => { cancelled = true; };
  }, [isAdmin]);

  useEffect(() => {
    let cancelled = false;
    listJuniors().then((rows) => {
      if (cancelled) return;
      if (rows.length) {
        setJuniors(rows);
        setUsingSeed(false);
      } else {
        setJuniors(SEED_JUNIORS);
        setUsingSeed(true);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    listProjects().then((rows) => {
      if (cancelled) return;
      setProjects(rows || []);
    }).catch((err) => {
      console.error("[staffing] listProjects failed:", err);
    });
    return () => { cancelled = true; };
  }, []);

  const onAssign = async (juniorId, projectId) => {
    const normalizedId = projectId || null;
    const prevJunior = juniors.find((j) => j.id === juniorId);
    const prevProjectId = prevJunior ? (prevJunior.assignedProjectId || null) : null;
    const prevStatus = prevJunior ? prevJunior.status : "Available";
    setJuniors((prev) => prev.map((j) => j.id === juniorId
      ? { ...j, assignedProjectId: normalizedId, status: normalizedId ? "Assigned" : "Available" }
      : j));
    setAssignErrors((e) => { const next = { ...e }; delete next[juniorId]; return next; });
    try {
      await assignJuniorToProject(juniorId, normalizedId);
    } catch (err) {
      console.error("[staffing] assign-to-project failed:", err);
      setJuniors((prev) => prev.map((j) => j.id === juniorId
        ? { ...j, assignedProjectId: prevProjectId, status: prevStatus }
        : j));
      setAssignErrors((e) => ({ ...e, [juniorId]: (err && err.code) || "assign failed" }));
    }
  };

  const handleApprove = async (cardId) => {
    setPendingActions((a) => ({ ...a, [cardId]: "approving" }));
    try {
      await approveJunior(cardId);
      setPendingJuniors((prev) => prev.filter((j) => j.id !== cardId));
      setPendingActions((a) => { const next = { ...a }; delete next[cardId]; return next; });
      // refetch the main roster so the approved junior shows up immediately
      listJuniors().then((rows) => {
        if (rows.length) { setJuniors(rows); setUsingSeed(false); }
      }).catch(() => {});
    } catch (err) {
      const msg = (err && err.message) || "approve failed";
      setPendingActions((a) => ({ ...a, [cardId]: `error:${msg}` }));
    }
  };

  const handleReject = async (cardId) => {
    setPendingActions((a) => ({ ...a, [cardId]: "rejecting" }));
    try {
      await rejectJunior(cardId);
      setPendingJuniors((prev) => prev.filter((j) => j.id !== cardId));
      setPendingActions((a) => { const next = { ...a }; delete next[cardId]; return next; });
    } catch (err) {
      const msg = (err && err.message) || "reject failed";
      setPendingActions((a) => ({ ...a, [cardId]: `error:${msg}` }));
    }
  };

  const filtered = useMemo(() => {
    const lc = search.trim().toLowerCase();
    return juniors.filter((j) => {
      if (j.offboarded) return false;
      if (filter !== "all" && j.status !== filter) return false;
      if (!lc) return true;
      return (
        j.name.toLowerCase().includes(lc) ||
        j.skills.some((s) => s.toLowerCase().includes(lc))
      );
    });
  }, [juniors, filter, search]);

  const handleAssign = async (id) => {
    setJuniors((prev) => prev.map((j) => j.id === id ? { ...j, status: "assigned" } : j));
    try {
      await updateJuniorStatus(id, "assigned");
    } catch (err) {
      console.error("[staffing] assign failed:", err);
    }
  };

  const handleStartNew = () => setEditing({ ...emptyDraft(), id: null });
  const handleStartEdit = (j) => setEditing(toDraft(j));
  const handleCancelEdit = () => setEditing(null);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editing || !editing.name.trim()) return;
    setSaving(true);
    try {
      const doc = draftToDoc(editing);
      if (editing.id) {
        await updateJunior(editing.id, doc);
        setJuniors((prev) => prev.map((j) => j.id === editing.id ? { ...j, ...doc } : j));
      } else {
        const id = await addJunior(doc);
        if (id) {
          setJuniors((prev) => [{ id, ...doc }, ...prev.filter((_j) => !usingSeed)]);
          setUsingSeed(false);
        }
      }
      setEditing(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[staffing] save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (typeof window !== "undefined" && !window.confirm("Delete this junior?")) return;
    try {
      await deleteJunior(id);
      setJuniors((prev) => prev.filter((j) => j.id !== id));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[staffing] delete failed:", err);
    }
  };

  const setDraftField = (field, value) => setEditing((d) => ({ ...d, [field]: value }));

  return (
    <SectionStyled>
      <Container>
        <Label>JUNIOR STAFFING</Label>
        <Title>Roster</Title>
        <Subtitle>
          Browse, filter, and assign juniors to client engagements.
          {isAdmin && " You can add, edit, or remove juniors here — no Firestore required."}
        </Subtitle>

        <Instructions label="how to use staffing">
          <h4>What this page does</h4>
          <p>
            Our roster of junior engineers — who they are, what they
            know, whether they're free. Clients browse here when they
            need an extra pair of hands.
          </p>

          <h4>Statuses, in plain English</h4>
          <ul>
            <li><b>Available</b> — free now. Can start next week.</li>
            <li><b>Placed</b> — already working on a client engagement.</li>
            <li><b>Training</b> — in bootcamp. Not for client work yet.</li>
            <li><b>Archived</b> — left the team. Kept for history.</li>
          </ul>

          <h4>Use case · "Client needs a Kubernetes hand for 3 months"</h4>
          <p>
            Filter by <i>Available</i>, look for the Kubernetes chip on
            their stack. Click contact, propose the engagement, flip them
            to Placed when the contract is signed.
          </p>

          <h4>Use case · "Junior finishes a project"</h4>
          <p>
            Flip them from Placed back to Available. They show up to the
            next client looking for help.
          </p>

          <h4>Use case · "Star junior gets hired full-time by client"</h4>
          <p>
            Great outcome — that's the point of mentorship. Flip them to
            Archived with a note. We celebrate, then go find the next one.
          </p>

          <h4>Admin · use case "New junior just joined"</h4>
          <p>
            Click <b>+ Add junior</b>, fill the form: name, role, stack,
            rate, status Training (or Available if they're ready). They
            show up on the roster immediately. Add a note about who their
            mentor is.
          </p>

          <h4>Admin · use case "Won a deal, need to staff it"</h4>
          <p>
            Make sure the project exists in Projects (create it if not).
            Come back here, find an Available junior whose stack fits, and
            use the <b>Assigned project</b> dropdown to attach them. Their
            status flips to Assigned automatically.
          </p>

          <h4>Admin · use case "Rotating a junior off a project"</h4>
          <p>
            Find their row, change the dropdown to <i>(unassigned)</i>.
            Status flips back to Available. They're ready for the next
            engagement.
          </p>

          <h4>Admin · use case "Updating skills after a project"</h4>
          <p>
            Open their row, edit the stack chips to reflect what they
            actually used. The roster is a live snapshot — what they
            knew 6 months ago isn't what they know today.
          </p>

          <h4>Admin · use case "Junior is taking a sabbatical / left"</h4>
          <p>
            Don't delete. Set status to Archived. We keep the history so
            we can re-engage later or learn from outcomes.
          </p>

          <h4>If you're a client or staff member</h4>
          <p>
            View-only. Filter by stack to find a fit, click contact to
            start a conversation about hiring or placement.
          </p>
        </Instructions>

        {isAdmin && (
          <AdminToolbar>
            <AddBtn type="button" onClick={handleStartNew} disabled={editing !== null}>
              + Add junior
            </AddBtn>
          </AdminToolbar>
        )}

        {isAdmin && editing !== null && (
          <FormCard onSubmit={handleSave}>
            <FormField>
              <FormLabel>Name</FormLabel>
              <FormInput
                type="text"
                value={editing.name}
                onChange={(e) => setDraftField("name", e.target.value)}
                required
              />
            </FormField>
            <FormField>
              <FormLabel>Role</FormLabel>
              <FormInput
                type="text"
                value={editing.role}
                onChange={(e) => setDraftField("role", e.target.value)}
              />
            </FormField>
            <FormField>
              <FormLabel>Location</FormLabel>
              <FormInput
                type="text"
                value={editing.location}
                onChange={(e) => setDraftField("location", e.target.value)}
              />
            </FormField>
            <FormField>
              <FormLabel>Years experience</FormLabel>
              <FormInput
                type="number"
                min="0"
                step="1"
                value={editing.yearsOfExperience}
                onChange={(e) => setDraftField("yearsOfExperience", e.target.value)}
              />
            </FormField>
            <FormField>
              <FormLabel>Rate</FormLabel>
              <FormInput
                type="text"
                placeholder="$30/h"
                value={editing.rate}
                onChange={(e) => setDraftField("rate", e.target.value)}
              />
            </FormField>
            <FormField>
              <FormLabel>Status</FormLabel>
              <FormSelect
                value={editing.status}
                onChange={(e) => setDraftField("status", e.target.value)}
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </FormSelect>
            </FormField>
            <FormField style={{ gridColumn: "1 / -1" }}>
              <FormLabel>Skills (comma-separated)</FormLabel>
              <FormInput
                type="text"
                placeholder="terraform, aws, kubernetes basics"
                value={editing.skills}
                onChange={(e) => setDraftField("skills", e.target.value)}
              />
            </FormField>
            <FormField style={{ gridColumn: "1 / -1" }}>
              <FormLabel>Bio</FormLabel>
              <FormTextArea
                value={editing.bio}
                onChange={(e) => setDraftField("bio", e.target.value)}
              />
            </FormField>
            <FormActions>
              <FormCancelBtn type="button" onClick={handleCancelEdit}>Cancel</FormCancelBtn>
              <FormSaveBtn type="submit" disabled={saving || !editing.name.trim()}>
                {saving ? "Saving…" : (editing.id ? "Save changes" : "Add junior")}
              </FormSaveBtn>
            </FormActions>
          </FormCard>
        )}

        {isAdmin && pendingJuniors.length > 0 && (
          <PendingSection>
            <PendingKicker>PENDING APPROVAL ({pendingJuniors.length})</PendingKicker>
            <Row>
              {pendingJuniors.map((j) => {
                const action = pendingActions[j.id];
                const busy = action === "approving" || action === "rejecting";
                const errorMsg = action && action.startsWith("error:") ? action.slice(6) : null;
                return (
                  <Col md="6" lg="4" className="mb-4" key={j.id}>
                    <Card>
                      <CardHeader>
                        <CardName>{j.name}</CardName>
                        <StatusBadge status="off">pending</StatusBadge>
                      </CardHeader>
                      <CardMeta>
                        {[j.role, j.yearsOfExperience != null && `${j.yearsOfExperience}y`, j.location]
                          .filter(Boolean).join(" · ")}
                      </CardMeta>
                      <Bio>{j.bio}</Bio>
                      {Array.isArray(j.skills) && j.skills.length > 0 && (
                        <SkillRow>
                          {j.skills.map((s) => <SkillChip key={s}>{s}</SkillChip>)}
                        </SkillRow>
                      )}
                      {Array.isArray(j.learningInterests) && j.learningInterests.length > 0 && (
                        <>
                          <WantsToLearnLabel>Wants to learn</WantsToLearnLabel>
                          <SkillRow>
                            {j.learningInterests.map((s) => <SkillChip key={s}>{s}</SkillChip>)}
                          </SkillRow>
                        </>
                      )}
                      <PendingCardAdminRow>
                        <ApproveBtn
                          type="button"
                          disabled={busy}
                          onClick={() => handleApprove(j.id)}
                        >
                          {action === "approving" ? "Approving…" : "Approve"}
                        </ApproveBtn>
                        <DangerBtn
                          type="button"
                          disabled={busy}
                          onClick={() => handleReject(j.id)}
                        >
                          {action === "rejecting" ? "Rejecting…" : "Reject"}
                        </DangerBtn>
                        {errorMsg && (
                          <AssignError>{errorMsg}</AssignError>
                        )}
                      </PendingCardAdminRow>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </PendingSection>
        )}

        <FilterRow>
          {FILTERS.map((f) => (
            <Chip key={f} active={filter === f} onClick={() => setFilter(f)}>{f}</Chip>
          ))}
        </FilterRow>

        <SearchInput
          type="text"
          placeholder="Search by name or skill"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {loading ? (
          <Empty>Loading…</Empty>
        ) : filtered.length === 0 ? (
          <Empty>No juniors match those filters</Empty>
        ) : (
          <Row>
            {filtered.map((j) => (
              <Col md="6" lg="4" className="mb-4" key={j.id}>
                <Card>
                  <CardHeader>
                    <CardName>{j.name}</CardName>
                    <StatusBadge status={j.status}>{j.status}</StatusBadge>
                  </CardHeader>
                  <CardMeta>{j.role} · {j.yearsOfExperience}y · {j.location}</CardMeta>
                  <Bio>{j.bio}</Bio>
                  <SkillRow>
                    {j.skills.map((s) => <SkillChip key={s}>{s}</SkillChip>)}
                  </SkillRow>
                  {Array.isArray(j.learningInterests) && j.learningInterests.length > 0 && (
                    <>
                      <WantsToLearnLabel>Wants to learn</WantsToLearnLabel>
                      <SkillRow>
                        {j.learningInterests.map((s) => <SkillChip key={s}>{s}</SkillChip>)}
                      </SkillRow>
                    </>
                  )}
                  {isAdmin && (
                    <AssignField>
                      <AssignLabel>Assigned project</AssignLabel>
                      <AssignSelect
                        value={j.assignedProjectId || ""}
                        onChange={(e) => onAssign(j.id, e.target.value)}
                      >
                        <option value="">(unassigned)</option>
                        {projects.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.company} — {p.name}
                          </option>
                        ))}
                      </AssignSelect>
                      {assignErrors[j.id] && (
                        <AssignError>assign failed · {assignErrors[j.id]}</AssignError>
                      )}
                    </AssignField>
                  )}
                  <CardFooter>
                    <Rate>{j.rate}</Rate>
                    <AssignButton
                      type="button"
                      onClick={() => handleAssign(j.id)}
                      disabled={j.status === "assigned"}
                    >
                      {j.status === "assigned" ? "Assigned" : "Assign"}
                    </AssignButton>
                  </CardFooter>
                  {isAdmin && !usingSeed && (
                    <CardAdminRow>
                      <SmallBtn type="button" onClick={() => handleStartEdit(j)}>Edit</SmallBtn>
                      <DangerBtn type="button" onClick={() => handleDelete(j.id)}>Delete</DangerBtn>
                    </CardAdminRow>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </SectionStyled>
  );
};

export default JuniorStaffing;
